import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize.tsx';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal.ts';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions.ts';
import { type SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealFormContext } from '../../../../DealForm.context.tsx';
import { getDealsLabels } from '../../../../utils/dealFormUtils.ts';

export type PackageSelectProps = {
  curatedOnly?: boolean;
};

export const PackageSelect = ({ curatedOnly = false }: PackageSelectProps) => {
  const { pckgFetch, pckg, pckgUid, targetablePckgs, targetableCuratedPckgs, targetablePckgsAreLoading } =
    useDealFormContext();

  const pckgs = useMemo(() => {
    return curatedOnly ? targetableCuratedPckgs : targetablePckgs;
  }, [targetablePckgs, targetableCuratedPckgs, curatedOnly]);

  const pckgsSortedOptions = useMemo(() => produceSortedOptionsKeys(pckgs), [pckgs]);

  const { t } = useTranslation();

  const {
    setError,
    clearErrors,
    formState: { isValidating },
  } = useFormContext<DealFormDataWithTargeting>();

  const dealsLabels = useMemo(() => getDealsLabels(t), [t]);

  const handleChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      pckgFetch(value);
    },
    [pckgFetch]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <setError, clearErrors and t are should not be included>
  useEffect(() => {
    if (isValidating) {
      return;
    }

    if (!pckg?.uid) {
      setError(dealFormField.PACKAGE_UID, { type: 'required', message: t('Package is required') });
    } else {
      clearErrors(dealFormField.PACKAGE_UID);
    }
  }, [pckg?.uid, isValidating]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <clearErrors should not be included>
  useEffect(() => {
    return () => {
      clearErrors(dealFormField.PACKAGE_UID);
    };
  }, []);

  return (
    <AutoCompleteVirtualize
      name={dealFormField.PACKAGE_UID}
      textFieldProps={{
        label: dealsLabels[dealFormField.PACKAGE_UID],
        placeholder: t('[ Select a Package ]'),
        error: !pckg?.uid,
        helperText: !pckg?.uid ? t('Package is required') : '',
      }}
      disableClearable
      renderOptions={{
        dataTest: 'deal-packages-item',
        tooltip: true,
      }}
      getOptionLabel={option => pckgs[option]?.name || t('N / A')}
      value={pckgUid || undefined}
      options={pckgsSortedOptions}
      loading={targetablePckgsAreLoading}
      data-test="packages"
      onChange={handleChange}
    />
  );
};
